/**
 * Packaging Configuration Page
 * Allows manufacturers to configure packaging hierarchy for product batches
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { packagingService } from '../services/packaging.service';
import PackagingConfigForm, { PackagingConfigFormData } from '../components/PackagingConfigForm';
import { Alert, Button, PageHeader } from '../components/ui';

export default function PackagingConfigPage() {
  const navigate = useNavigate();
  const { batchId } = useParams<{ batchId: string }>();

  const [existingConfig, setExistingConfig] = useState<PackagingConfigFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (batchId) {
      loadConfig();
    } else {
      setLoading(false);
    }
  }, [batchId]);

  const loadConfig = async () => {
    if (!batchId) return;

    try {
      setLoading(true);
      setError(null);
      const result = await packagingService.getConfig(batchId);
      if (result.success && result.data) {
        setExistingConfig(result.data);
      }
    } catch (err: any) {
      console.error('Failed to load config:', err);
      // Config might not exist yet, which is fine
      if (err.response?.status !== 404) {
        setError(err.response?.data?.error || 'Failed to load configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: PackagingConfigFormData) => {
    if (!batchId) {
      setError('Batch ID is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const result = existingConfig
        ? await packagingService.updateConfig(batchId, data)
        : await packagingService.createConfig(batchId, data);

      if (result.success) {
        setSuccess(existingConfig ? 'Configuration updated successfully!' : 'Configuration created successfully!');
        setExistingConfig(result.data);
        setTimeout(() => {
          navigate(`/products/batch/${batchId}`);
        }, 1500);
      } else {
        setError(result.error || 'Failed to save configuration');
      }
    } catch (err: any) {
      console.error('Failed to save config:', err);
      setError(err.response?.data?.error || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!batchId || !existingConfig) return;

    if (!confirm('Are you sure you want to delete this packaging configuration?')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const result = await packagingService.deleteConfig(batchId);
      if (result.success) {
        setSuccess('Configuration deleted successfully!');
        setTimeout(() => {
          navigate(`/products/batch/${batchId}`);
        }, 1500);
      } else {
        setError(result.error || 'Failed to delete configuration');
      }
    } catch (err: any) {
      console.error('Failed to delete config:', err);
      setError(err.response?.data?.error || 'Failed to delete configuration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          className="mb-6"
          title="Packaging Configuration"
          description={`${existingConfig ? 'Update' : 'Configure'} packaging hierarchy for bulk shipments.`}
          actions={
            <Button
              variant="outline"
              onClick={() => navigate(batchId ? `/products/batch/${batchId}` : '/dashboard')}
              disabled={saving}
            >
              Back
            </Button>
          }
        />

        {/* Success Alert */}
        {success && (
          <Alert variant="success" className="mb-6">
            {success}
          </Alert>
        )}

        {/* Error Alert */}
        {error && (
          <Alert variant="error" className="mb-6">
            {error}
          </Alert>
        )}

        {/* Form */}
        <div className="bg-white rounded-lg shadow p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              <PackagingConfigForm
                initialData={existingConfig || undefined}
                onSubmit={handleSubmit}
                onCancel={() => navigate(batchId ? `/products/batch/${batchId}` : '/dashboard')}
                loading={saving}
              />

              {/* Delete Button */}
              {existingConfig && (
                <div className="mt-6 pt-6 border-t">
                  <Button
                    variant="danger"
                    onClick={handleDelete}
                    disabled={saving}
                  >
                    Delete Configuration
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
    </div>
  );
}
